import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import Header from '../Common/Header';
import './VoteCounting.css';
import { useNavigate } from 'react-router-dom'; 

const VoteCounting = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('results');
  const [constituencies, setConstituencies] = useState([]);
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [results, setResults] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [partySummary, setPartySummary] = useState([]);
  const [notaSummary, setNotaSummary] = useState(null);
  const [loadingParties, setLoadingParties] = useState(true);

  const [countdown, setCountdown] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0, isActive: true
  });

  const dropdownRef = useRef(null);

  useEffect(() => {
    const targetDate = new Date('2025-11-10T00:00:00').getTime();
    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      if (difference <= 0) {
        setCountdown({ days:0, hours:0, minutes:0, seconds:0, isActive: false });
        return;
      }
      const days = Math.floor(difference / (1000*60*60*24));
      const hours = Math.floor((difference % (1000*60*60*24)) / (1000*60*60));
      const minutes = Math.floor((difference % (1000*60*60)) / (1000*60));
      const seconds = Math.floor((difference % (1000*60)) / 1000);
      setCountdown({ days, hours, minutes, seconds, isActive: true });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!countdown.isActive) {
      loadConstituencies();
      loadPartySummary();
      loadAllCandidates();
    }
  }, [countdown.isActive]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadConstituencies = async () => {
    try {
      const response = await api.getVotingConstituencies();
      if (response.success) {
        const sortedConstituencies = response.data.sort((a, b) => {
          const numA = parseInt(a.code.replace(/\D/g, ''), 10);
          const numB = parseInt(b.code.replace(/\D/g, ''), 10);
          return numA - numB;
        });
        setConstituencies(sortedConstituencies);
      }
    } catch (error) {
      console.error('Error loading constituencies:', error);
    }
  };

  const loadAllCandidates = async () => {
    setLoadingAll(true);
    try {
      const response = await api.getAllCandidatesWithResults();
      if (response.success) {
        // ✅ Sort by latest round votes instead of totalVotes
        const sortedCandidates = response.data.sort((a, b) => {
          const aLatest = a.rounds?.length ? a.rounds[a.rounds.length - 1].votes : 0;
          const bLatest = b.rounds?.length ? b.rounds[b.rounds.length - 1].votes : 0;
          return bLatest - aLatest;
        });
        setAllCandidates(sortedCandidates);
      }
    } catch (error) {
      console.error('Error loading all candidates:', error);
    } finally {
      setLoadingAll(false);
    }
  };

  const loadPartySummary = async () => {
    setLoadingParties(true);
    try {
      const response = await api.getPartySummary();
      if (response.success) {
        setPartySummary(response.data);
        const notaData = response.nota || { leadingSeats: 0, totalVotes: 0 };
        setNotaSummary(notaData);
      }
    } catch (error) {
      console.error('Error loading party summary:', error);
    } finally {
      setLoadingParties(false);
    }
  };

  const loadResults = async (constituencyId) => {
    setLoading(true);
    try {
      const response = await api.getConstituencyResults(constituencyId);
      if (response.success) {
        setResults(response.data);
      }
    } catch (error) {
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConstituencyChange = async (constituencyId, constituencyName) => {
    setSelectedConstituency(constituencyId);
    setSearchQuery(constituencyName);
    setShowDropdown(false);
    loadResults(constituencyId);
  };

  const handleClearConstituency = () => {
    setSearchQuery('');
    setSelectedConstituency(null);
    setResults([]);
  };

  const filteredConstituencies = constituencies.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) return photoUrl;
    const cleanPath = photoUrl.startsWith('/') ? photoUrl.substring(1) : photoUrl;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://localhost:7014';
    return `${baseUrl}/${cleanPath}`;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return name.substring(0, 2);
  };

  const getPartyColor = (partyShortName) => {
    const colors = {
      'BJP': '#FF9933',
      'INC': '#19AAED',
      'BSP': '#0000CD',
      'JD(U)': '#006400',
      'RJD': '#009000',
      'CPI': '#FF0000',
      'CPI(M)': '#CC0000',
      'AIIM': '#0072B0',
      'SP': '#FF2222',
      'TMC': '#20C997',
      'NCP': '#00B2AC',
    };
    return colors[partyShortName] || '#667eea';
  };

  const ndaParties = ['BJP', 'JD(U)', 'LJP(R)', 'RLM', 'HAM'];
  const indiaParties = ['INC', 'RJD', 'VIP', 'CPI', 'CPI(M)', 'CPI(ML)','IIP'];
  const otherParties = ['AIIM', 'JSP', 'JJD', 'BSP', 'IND', 'Others'];

  const groupPartiesByAlliance = (parties, notaVotesData) => {
    const nda = parties.filter(p => ndaParties.includes(p.partyShortName));
    const india = parties.filter(p => indiaParties.includes(p.partyShortName));
    const others = parties.filter(p => otherParties.includes(p.partyShortName));

    const notaTotalSeats = notaVotesData ? notaVotesData.leadingSeats || 0 : 0;
    const notaTotalVotes = notaVotesData ? notaVotesData.totalVotes || 0 : 0;

    return [
      {
        name: 'NDA',
        fullName: t('dashboard.ndaAllianceFull', 'National Democratic Alliance'),
        parties: nda,
        totalSeats: nda.reduce((sum, p) => sum + p.leadingSeats, 0),
        totalVotes: nda.reduce((sum, p) => sum + p.totalVotes, 0),
        color: '#FF9933'
      },
      {
        name: 'INDIA',
        fullName: t('dashboard.indiaAllianceFull', 'India Alliance'),
        parties: india,
        totalSeats: india.reduce((sum, p) => sum + p.leadingSeats, 0),
        totalVotes: india.reduce((sum, p) => sum + p.totalVotes, 0),
        color: '#19AAED'
      },
      {
        name: t('dashboard.others', 'Others'),
        fullName: t('dashboard.othersFull', 'Other Parties'),
        parties: others,
        totalSeats: others.reduce((sum, p) => sum + p.leadingSeats, 0),
        totalVotes: others.reduce((sum, p) => sum + p.totalVotes, 0),
        color: '#64748b'
      },
      {
        name: 'NOTA',
        fullName: 'None Of The Above',
        parties: [],
        totalSeats: notaTotalSeats,
        totalVotes: notaTotalVotes,
        color: '#dc2626'
      }
    ];
  };

  const allianceData = groupPartiesByAlliance(partySummary, notaSummary);

  // ✅ Calculate total votes from LATEST rounds only
  const totalVotes = results.reduce((sum, r) => {
    const latestRound = r.rounds?.length ? r.rounds[r.rounds.length - 1] : null;
    return sum + (latestRound?.votes || 0);
  }, 0);

  const leadingCandidate = results[0];
  
  // ✅ Calculate margin using latest round votes
  const margin = results.length > 1 ? (() => {
    const first = results[0]?.rounds?.length ? results[0].rounds[results[0].rounds.length - 1].votes : 0;
    const second = results[1]?.rounds?.length ? results[1].rounds[results[1].rounds.length - 1].votes : 0;
    return first - second;
  })() : 0;

  const allianceVotesTotal = allianceData.reduce((sum, group) => sum + group.totalVotes, 0);

  const allianceWithPercentage = allianceData.map(group => ({
    ...group,
    percentage: allianceVotesTotal > 0 
      ? ((group.totalVotes / allianceVotesTotal) * 100).toFixed(2)
      : '0.00'
  }));

  // Helper: Get latest round's votes and isFinal status for a candidate
function getLatestRound(candidate) {
  if (!candidate.rounds || candidate.rounds.length === 0) {
    return { votes: 0, isFinal: false };
  }
  const latest = candidate.rounds[candidate.rounds.length - 1];
  return { votes: latest.votes, isFinal: !!latest.isFinal };
}

// Helper: Prepare and sort candidates with resolvedVotes and isFinal
function prepareCandidates(candidates) {
  return candidates.map((c) => {
    const { votes, isFinal } = getLatestRound(c);
    const resolvedVotes = c.latestVotes !== undefined ? c.latestVotes : votes;
    return { ...c, resolvedVotes, isFinal };
  }).sort((a, b) => b.resolvedVotes - a.resolvedVotes);
}

const sortedCandidates = prepareCandidates(results || []);
const isConstituencyFinal = sortedCandidates.some(c => c.isFinal);

  if (countdown.isActive) {
    return (
      <>
        <Header />
        <div className="vc-vote-counting-page">
          <div className="vc-countdown-container">
            <div className="vc-countdown-card">
              <div className="vc-countdown-icon">
                <i className="fas fa-clock"></i>
              </div>
              <h1 className="vc-countdown-title">
                {t('votecounting.countdown.title')}
              </h1>
              <p className="vc-countdown-subtitle">
                {t('votecounting.countdown.subtitle')}
              </p>
              <div className="vc-target-date">
                <i className="fas fa-calendar-alt"></i>
                {t('votecounting.countdown.targetDate')}
              </div>
              <div className="vc-countdown-timer">
                <div className="vc-time-box">
                  <div className="vc-time-value">{String(countdown.days).padStart(2, '0')}</div>
                  <div className="vc-time-label">{t('votecounting.countdown.days')}</div>
                </div>
                <div className="vc-time-separator">:</div>
                <div className="vc-time-box">
                  <div className="vc-time-value">{String(countdown.hours).padStart(2, '0')}</div>
                  <div className="vc-time-label">{t('votecounting.countdown.hours')}</div>
                </div>
                <div className="vc-time-separator">:</div>
                <div className="vc-time-box">
                  <div className="vc-time-value">{String(countdown.minutes).padStart(2, '0')}</div>
                  <div className="vc-time-label">{t('votecounting.countdown.minutes')}</div>
                </div>
                <div className="vc-time-separator">:</div>
                <div className="vc-time-box">
                  <div className="vc-time-value">{String(countdown.seconds).padStart(2, '0')}</div>
                  <div className="vc-time-label">{t('votecounting.countdown.seconds')}</div>
                </div>
              </div>
              <div className="vc-countdown-message">
                <i className="fas fa-info-circle"></i>
                <p>{t('votecounting.countdown.message')}</p>
              </div>
              <div className="vc-countdown-features">
                <div className="vc-feature-item">
                  <i className="fas fa-chart-line"></i>
                  <span>{t('votecounting.countdown.features.realtime')}</span>
                </div>
                <div className="vc-feature-item">
                  <i className="fas fa-map-marked-alt"></i>
                  <span>{t('votecounting.countdown.features.constituencies')}</span>
                </div>
                <div className="vc-feature-item">
                  <i className="fas fa-users"></i>
                  <span>{t('votecounting.countdown.features.candidates')}</span>
                </div>
                <div className="vc-feature-item">
                  <i className="fas fa-flag"></i>
                  <span>{t('votecounting.countdown.features.party')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="vc-vote-counting-page">
        <div className="vc-tabs-container">
          <button
            className={`vc-tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            <i className="fas fa-chart-line"></i>
            {t('votecounting.tabResults')}
          </button>
          <button
            className={`vc-tab ${activeTab === 'party' ? 'active' : ''}`}
            onClick={() => setActiveTab('party')}
          >
            <i className="fas fa-flag"></i>
            {t('votecounting.tabPartyWise')}
          </button>
        </div>

        <div className="vc-results-container">
          {activeTab === 'results' && (
            <>
              <div className="vc-constituency-selector" ref={dropdownRef}>
                <label>{t('results.selectConstituency')}</label>
                <div className="vc-search-input-wrapper">
                  <i className="fas fa-search vc-search-icon"></i>
                  <input
                    type="text"
                    className="vc-constituency-search"
                    placeholder={t('results.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                  />
                  {searchQuery && (
                    <button className="vc-clear-search" onClick={handleClearConstituency}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {showDropdown && (
                  <div className="vc-dropdown-list">
                    {filteredConstituencies.length > 0 ? (
                      filteredConstituencies.map(c => (
                        <div
                          key={c.id}
                          className={`vc-dropdown-item ${selectedConstituency === c.id ? 'vc-selected' : ''}`}
                          onClick={() => handleConstituencyChange(c.id, `${c.code} - ${c.name}`)}
                        >
                          <span className="vc-constituency-code">{c.code}</span>
                          <span className="vc-constituency-name">{c.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="vc-dropdown-item vc-no-results">
                        <i className="fas fa-search"></i>
                        {t('results.noConstituencies')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedConstituency ? (
                <>
                  {loading && results.length === 0 ? (
                    <div className="vc-loading-container">
                      <div className="vc-spinner"></div>
                      <p>{t('results.loading')}</p>
                    </div>
                  ) : results.length > 0 ? (
                    <>
                      <div className="vc-results-stats">
                        <div className="vc-stat-card">
                          <i className="fas fa-users"></i>
                          <div className="vc-stat-content">
                            <div className="vc-stat-value">{totalVotes.toLocaleString()}</div>
                            <div className="vc-stat-label">{t('results.totalVotes')}</div>
                          </div>
                        </div>
                        <div className="vc-stat-card">
                          <i className="fas fa-user-check"></i>
                          <div className="vc-stat-content">
                            <div className="vc-stat-value">{results.length}</div>
                            <div className="vc-stat-label">{t('results.candidates')}</div>
                          </div>
                        </div>
                        {results.length > 1 && (
                          <div className="vc-stat-card">
                            <i className="fas fa-chart-bar"></i>
                            <div className="vc-stat-content">
                              <div className="vc-stat-value">{margin.toLocaleString()}</div>
                              <div className="vc-stat-label">{t('results.leadMargin')}</div>
                            </div>
                          </div>
                        )}
                      </div>
                      {sortedCandidates.length > 0 && (() => {
  const leadingCandidate = sortedCandidates[0];
  const latestVotes = leadingCandidate.resolvedVotes || 0;

                        
                        return (
                          <div className="vc-leading-candidate-card">
                            <div className="vc-leading-badge-top">
  <i className="fas fa-crown"></i>
  {isConstituencyFinal ? t('results.winner') : t('results.winner')}
</div>

                            <div className="vc-leading-content">
                              <div className="vc-leading-photo">
                                {getImageUrl(leadingCandidate.photoUrl) ? (
                                  <img
                                    src={getImageUrl(leadingCandidate.photoUrl)}
                                    alt={leadingCandidate.candidateName}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className="vc-candidate-avatar-fallback" style={{ display: getImageUrl(leadingCandidate.photoUrl) ? 'none' : 'flex' }}>
                                  <i className="fas fa-user"></i>
                                </div>
                              </div>
                              <div className="vc-leading-info">
                                <h2>{leadingCandidate.candidateName}</h2>
                                <div className="vc-leading-party">{leadingCandidate.partyName}</div>
                                <div className="vc-leading-votes">
                                  <span className="vc-votes-number">{latestVotes.toLocaleString()}</span>
                                  <span className="vc-votes-label">{t('results.votes')}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="vc-results-list">
  <h3>{t('results.allCandidates')}</h3>
  {results
    // Sort by latest round votes (descending)
    .slice() // Copy so original not mutated
    .sort((a, b) => {
      const aVotes = a.rounds?.length ? a.rounds[a.rounds.length - 1].votes : 0;
      const bVotes = b.rounds?.length ? b.rounds[b.rounds.length - 1].votes : 0;
      return bVotes - aVotes;
    })
    .map((result, index) => {
      const latestRound = result.rounds?.length 
        ? result.rounds[result.rounds.length - 1] 
        : null;
      const latestVotes = latestRound?.votes || 0;
      const percentage = totalVotes > 0 
        ? ((latestVotes / totalVotes) * 100).toFixed(2)
        : '0';
      const imageUrl = getImageUrl(result.photoUrl);

      return (
        <div key={result.candidateId} className={`vc-result-item vc-rank-${index + 1}`}>
          <div className="vc-rank-badge">
            <span>#{index + 1}</span>
          </div>
          <div className="vc-candidate-photo">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={result.candidateName}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="vc-candidate-avatar-fallback" style={{ display: imageUrl ? 'none' : 'flex' }}>
              <i className="fas fa-user"></i>
            </div>
          </div>
          <div className="vc-candidate-details">
            <div className="vc-candidate-name">{result.candidateName}</div>
            <div className="vc-candidate-party">{result.partyShortName}</div>
          </div>
          <div className="vc-vote-info">
            <div className="vc-vote-count">{latestVotes.toLocaleString()}</div>
            <div className="vc-vote-bar">
              <div className="vc-vote-bar-fill" style={{ width: `${percentage}%` }}></div>
            </div>
            <div className="vc-vote-percentage">{percentage}%</div>
          </div>
        </div>
      );
    })}
</div>

                    </>
                  ) : (
                    <div className="vc-no-results">
                      <i className="fas fa-inbox"></i>
                      <h3>{t('results.noResults')}</h3>
                      <p>{t('results.noResultsText')}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {loadingAll ? (
                    <div className="vc-loading-container">
                      <div className="vc-spinner"></div>
                      <p>{t('results.loadingAllCandidates')}</p>
                    </div>
                  ) : allCandidates.length > 0 ? (
                    <>
                      <div className="vc-all-candidates-header">
                        <h2>
                          <i className="fas fa-users"></i>
                          {t('votecounting.allCandidatesHeader')}
                        </h2>
                      </div>
                      {(() => {
                        const groupedCandidates = allCandidates.reduce((acc, candidate) => {
                          const key = candidate.constituencyCode;
                          if (!acc[key]) {
                            acc[key] = {
                              constituencyCode: candidate.constituencyCode,
                              constituencyName: candidate.constituencyName,
                              candidates: []
                            };
                          }
                          acc[key].candidates.push(candidate);
                          return acc;
                        }, {});

                        const sortedConstituencies = Object.values(groupedCandidates).sort((a, b) => {
                          const numA = parseInt(a.constituencyCode.replace(/\D/g, ''), 10);
                          const numB = parseInt(b.constituencyCode.replace(/\D/g, ''), 10);
                          return numA - numB;
                        });

                       return (
  <div className="vc-candidates-grouped-by-constituency">
    {sortedConstituencies.map(({ constituencyCode, constituencyName, candidates }) => (
      <div key={constituencyCode} className="vc-constituency-group">
        <h2 className="vc-constituency-section-header">
          <i className="fas fa-map-marker-alt"></i>
          {constituencyCode} - {constituencyName}
        </h2>
        <div className="vc-candidates-grid-view">
          {candidates
            .sort((a, b) => {
              const aLatest = a.rounds?.length ? a.rounds[a.rounds.length - 1].votes : 0;
              const bLatest = b.rounds?.length ? b.rounds[b.rounds.length - 1].votes : 0;
              return bLatest - aLatest;
            })
            .map((candidate, index) => {
              const imageUrl = getImageUrl(candidate.photoUrl);
              const partyLogoUrl = getImageUrl(candidate.symbolUrl);
              const partyColor = getPartyColor(candidate.partyShortName);

              const latestRound = candidate.rounds?.length
                ? candidate.rounds[candidate.rounds.length - 1]
                : null;
              const latestVotes = latestRound?.votes || 0;

              return (
                <div
                  key={candidate.candidateId}
                  className={`vc-candidate-card${index === 0 ? ' vc-winner-card' : ''}`}
                  style={{ borderLeftColor: partyColor, cursor: 'pointer' }}
                  onClick={() => navigate(`/candidate/${candidate.candidateId}`)}
                  tabIndex={0}
                  role="button"
                  onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(`/candidate/${candidate.candidateId}`);
                    }
                  }}
                >
                  {index === 0 && (
                    <div className="vc-winner-badge">
                      <i className="fas fa-crown"></i> WINNER
                    </div>
                  )}
                  <div className="vc-vote-count-badge">
                    <div className="vc-vote-number">{latestVotes.toLocaleString()}</div>
                  </div>
                  <div className="vc-card-header">
                    <div
                      className="vc-candidate-photo"
                      style={{ background: `linear-gradient(135deg, ${partyColor}, ${partyColor}dd)` }}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={candidate.candidateName}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span
                        className="vc-initials"
                        style={{ display: imageUrl ? 'none' : 'flex' }}
                      >
                        {getInitials(candidate.candidateName)}
                      </span>
                    </div>
                    <div className="vc-candidate-info">
                      <h3>{candidate.candidateName}</h3>
                      <span className="vc-constituency-name">{constituencyCode} - {constituencyName}</span>
                    </div>
                  </div>
                  <div
                    className="vc-party-info-bar"
                    style={{ background: `linear-gradient(135deg, ${partyColor}, ${partyColor}ee)` }}
                  >
                    <div className="vc-party-left">
                      <div className="vc-party-badge">{candidate.partyShortName}</div>
                      <div className="vc-party-full-name">{candidate.partyName}</div>
                    </div>
                    <div className="vc-party-symbol-badge">
                      {partyLogoUrl ? (
                        <img
                          src={partyLogoUrl}
                          alt={`${candidate.partyShortName} symbol`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="vc-party-symbol-fallback"
                        style={{ display: partyLogoUrl ? 'none' : 'flex' }}
                      >
                        <i className="fas fa-flag"></i>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    ))}
  </div>
);


                      })()}
                    </>
                  ) : (
                    <div className="vc-no-results">
                      <i className="fas fa-inbox"></i>
                      <h3>{t('results.noCandidatesData')}</h3>
                      <p>{t('results.noCandidatesText')}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'party' && (
            <div className="vc-party-dashboard">
              <div className="vc-dashboard-header">
                <h2>
                  <i className="fas fa-poll"></i>
                  {t('results.partyWise')}
                </h2>
                <p>{t('results.dashboardDesc')}</p>
              </div>
              {loadingParties ? (
                <div className="vc-loading-container">
                  <div className="vc-spinner"></div>
                  <p>{t('results.loadingParties')}</p>
                </div>
              ) : partySummary.length > 0 ? (
                <>
                  {(() => {
                    const allianceVotesTotal = allianceData.reduce((sum, group) => sum + group.totalVotes, 0);
                    var allianceWithPercentage = allianceData.map(group => ({
                      ...group,
                      percentage: allianceVotesTotal > 0 
                        ? ((group.totalVotes / allianceVotesTotal) * 100).toFixed(2)
                        : '0.00'
                    }));
                    return (
                      <div className="vc-alliance-chart-container">
                        <h3 className="vc-chart-title">
                          <i className="fas fa-chart-bar"></i>
                          {t('votecounting.alliancePerformance')}
                        </h3>
                        <div className="vc-alliance-chart">
                          {allianceWithPercentage.map((alliance) => (
                            <div key={alliance.name} className="vc-alliance-group">
                              <div className="vc-alliance-header">
                                <h4 style={{ color: alliance.color }}>{alliance.name}</h4>
                                <p>{alliance.fullName}</p>
                              </div>
                              <div className="vc-alliance-bars-container">
                                {alliance.parties.map((party) => {
                                  const maxSeats = Math.max(...partySummary.map(p => p.leadingSeats));
                                  const barHeight = maxSeats > 0 ? (party.leadingSeats / maxSeats) * 100 : 0;
                                  const partyColor = getPartyColor(party.partyShortName);
                                  return (
                                    <div key={party.partyId} className="vc-alliance-bar-item">
                                      <div className="vc-alliance-bar-wrapper">
                                        <div
                                          className="vc-alliance-bar-fill"
                                          style={{
                                            height: `${barHeight}%`,
                                            background: `linear-gradient(180deg, ${partyColor}, ${partyColor}dd)`
                                          }}
                                        >
                                          <div className="vc-alliance-bar-value">{party.leadingSeats}</div>
                                        </div>
                                      </div>
                                      <div className="vc-alliance-bar-label">{party.partyShortName}</div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="vc-alliance-footer" style={{ borderTopColor: alliance.color }}>
                                <div className="vc-alliance-total">
                                  <span className="vc-alliance-seats">{alliance.totalSeats}</span>
                                  <span className="vc-alliance-label">{t('votecounting.totalSeats')}</span>
                                </div>
                                <div className="vc-alliance-votes">
                                  {alliance.totalVotes.toLocaleString()} {t('results.votes')}
                                  <span style={{ color: alliance.color, marginLeft: 6 }}>
                                    ({alliance.percentage}%)
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="vc-party-stats-grid">
                    {partySummary.slice(0, 4).map((party, index) => (
                      <div key={party.partyId} className={`vc-party-stat-card vc-party-rank-${index + 1}`}>
                        <div className="vc-party-rank">#{index + 1}</div>
                        <div className="vc-party-logo">
                          {party.logoUrl ? (
                            <img src={getImageUrl(party.logoUrl)} alt={party.partyName} />
                          ) : (
                            <div className="vc-party-logo-fallback">
                              <i className="fas fa-flag"></i>
                            </div>
                          )}
                        </div>
                        <h3>{party.partyName}</h3>
                        <div className="vc-party-short-name">{party.partyShortName}</div>
                        <div className="vc-party-seats">
                          <span className="vc-seats-number">{party.leadingSeats}</span>
                          <span className="vc-seats-label">{t('results.winner')}</span>
                        </div>
                        <div className="vc-party-votes">
                          <i className="fas fa-users"></i>
                          {party.totalVotes.toLocaleString()} {t('results.votes')}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="vc-all-parties-table">
                    <h3>{t('results.allParties')}</h3>
                    <div className="vc-parties-table-wrapper">
                      <table className="vc-parties-table">
                        <thead>
                          <tr>
                            <th>{t('votecounting.rank')}</th>
                            <th>{t('votecounting.partyName')}</th>
                            <th>{t('votecounting.leadingSeats')}</th>
                            <th>{t('votecounting.totalVotes')}</th>
                            <th>{t('votecounting.voteShare')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {partySummary.map((party, index) => {
                            const totalVotesAllParties = partySummary.reduce((sum, p) => sum + p.totalVotes, 0);
                            const voteShare = totalVotesAllParties > 0
                              ? ((party.totalVotes / totalVotesAllParties) * 100).toFixed(2)
                              : '0.00';
                            return (
                              <tr key={party.partyId} className={index < 3 ? `vc-top-party-${index + 1}` : ''}>
                                <td><span className="vc-table-rank">#{index + 1}</span></td>
                                <td>
                                  <div className="vc-party-name-cell">
                                    {party.logoUrl && (
                                      <img src={getImageUrl(party.logoUrl)} alt={party.partyName} className="vc-party-mini-logo" />
                                    )}
                                    <div>
                                      <div className="vc-party-full-name">{party.partyName}</div>
                                      <div className="vc-party-code">{party.partyShortName}</div>
                                    </div>
                                  </div>
                                </td>
                                <td><span className="vc-seats-badge">{party.leadingSeats}</span></td>
                                <td className="vc-votes-cell">{party.totalVotes.toLocaleString()}</td>
                                <td>
                                  <div className="vc-vote-share-cell">
                                    <div className="vc-vote-share-bar">
                                      <div className="vc-vote-share-fill" style={{ width: `${voteShare}%` }}></div>
                                    </div>
                                    <span className="vc-vote-share-percent">{voteShare}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="vc-no-results">
                  <i className="fas fa-inbox"></i>
                  <h3>{t('results.noData')}</h3>
                  <p>{t('results.noDataText')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VoteCounting;
